import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './../../components/Footer';

const RebalanceGuide = () => {
  const title = "Portföy Yeniden Dengeleme Rehberi | Smart Portfoy";
  const description = "Portföyünüzü nasıl yeniden dengeleyeceğinizi öğrenin. Kapsamlı yeniden dengeleme stratejileri ve portföy yönetimi rehberi.";
  const currentYear = new Date().getFullYear();

  const strategies = [
    {
      title: 'Takvim Tabanlı',
      description: 'Belirli aralıklarla (örneğin, yıllık veya üç aylık) yeniden dengeleme yapılır.'
    },
    {
      title: 'Eşik Tabanlı',
      description: 'Varlık dağılımındaki sapma belirli bir yüzdeyi aştığında yeniden dengeleme yapılır.'
    },
    {
      title: 'Hibrit Strateji',
      description: 'Takvim ve eşik tabanlı stratejilerin bir kombinasyonu uygulanır.'
    }
  ];

  const smartFeatures = [
    {
      title: 'Otomatik Takip',
      features: ['7/24 portföy izleme', 'Gerçek zamanlı takip', 'Anlık bildirimler']
    },
    {
      title: 'Akıllı Analiz',
      features: ['Risk değerlendirmesi', 'Performans analizi', 'Optimizasyon önerileri']
    }
  ];

  const steps = [
    'Mevcut Dağılımı Değerlendirin',
    'Sapmaları Belirleyin',
    'İşlemleri Gerçekleştirin',
    'Düzenli Takip Edin'
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="/rebalance-guide" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href="https://smart-portfoy.com/egitim/yeniden-dengeleme-rehberi" />
        <meta name="keywords" content="portföy yönetimi, yeniden dengeleme, portföy optimizasyonu, yatırım stratejisi, portföy takibi" />
        <style>
          {`
            h1, h2, h3, h4, h5, h6 {
              text-decoration: none !important;
            }
          `}
        </style>
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-purple-900 py-32">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 no-underline select-none">
              Portföyünüzü Nasıl Yeniden Dengelersiniz?
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
              Portföy yeniden dengeleme, yatırım varlıklarınızın hedef dağılımına geri dönmesini sağlamanın önemli bir yoludur. Bu rehberde, nasıl ve neden yeniden dengeleme yapmanız gerektiğini öğrenin.
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-48"> {/* Reduced height */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/blog/rebalance/rebalance-guide.jpeg" 
            alt="Portföy Yeniden Dengeleme İllüstrasyonu"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-16">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Portföy Yeniden Dengeleme Nedir?
                </h2>
                <p className="text-gray-300 text-lg">
                  Portföy yeniden dengeleme, portföyünüzdeki varlıkların hedeflenen dağılımını korumak için yapılan bir işlemdir. Zamanla, piyasa hareketleri nedeniyle varlıkların dağılımı değişebilir. Yeniden dengeleme, risk yönetimi ve yatırım hedeflerinize uygun bir portföy tutmanıza yardımcı olur.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Yeniden Dengeleme Stratejileri
                </h2>
                <div className="space-y-4">
                  {strategies.map((strategy, index) => (
                    <div key={index} className="bg-gray-800 p-6 rounded-lg">
                      <h3 className="text-xl font-bold mb-2 text-purple-400 no-underline">
                        {strategy.title}
                      </h3>
                      <p className="text-gray-300">{strategy.description}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Smart Portfoy ile Otomatik Yeniden Dengeleme
                </h2>
                <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-8 rounded-lg">
                  <div className="space-y-6">
                    {smartFeatures.map((section, index) => (
                      <div key={index} className="bg-gray-800/50 p-6 rounded-lg">
                        <h3 className="text-xl font-bold mb-4 text-purple-400 no-underline">
                          {section.title}
                        </h3>
                        <ul className="space-y-2">
                          {section.features.map((feature, fIndex) => (
                            <li key={fIndex} className="flex items-center text-gray-300">
                              <span className="mr-2">•</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="bg-gray-800 p-8 rounded-lg">
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Adım Adım Yeniden Dengeleme
                </h2>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                        {index + 1}
                      </span>
                      <span className="text-gray-300">{step}</span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Portföyünüzü Nasıl Yeniden Dengelersiniz?",
            "description": description,
            "author": {
              "@type": "Organization",
              "name": "Smart Portfoy"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Smart Portfoy"
            },
            "datePublished": `${currentYear}-01-24T00:00:00Z`,
            "dateModified": `${currentYear}-01-24T00:00:00Z`,
            "image": "/images/blog/rebalance/rebalance-guide.jpeg",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "/rebalance-guide"
            }
          })}
        </script>
      </div>

      <Footer />
    </>
  );
};

export default RebalanceGuide;